import { useToast } from 'primevue/usetoast';
import { useUpdateStaffStatusMutation } from '@/types/vue-apollo-helpers';
import { StaffStatus } from '@/types/composition-functions';
import { useFormErrors } from '@/composables/useFormErrors';
import { extractGraphQLError } from '@/utils/graphqlError';

/**
 * Deactivate / reactivate a staff membership in the current school (#225).
 * Deactivation revokes every grant the member holds here and blocks this
 * school's context; reactivation restores entry only — roles are re-granted
 * deliberately. The optional reason lands in the audit row.
 */
export function useUpdateStaffStatus() {
  const toast = useToast();
  const { serverErrors, handleServerErrors, getFirstErrorMessage } = useFormErrors();
  const { mutate } = useUpdateStaffStatusMutation();
  const updatingId = ref<string | null>(null);

  async function setStatus(staffId: string, status: StaffStatus, reason?: string | null): Promise<boolean> {
    serverErrors.value = {};
    updatingId.value = staffId;
    try {
      await mutate({ staffId, status, reason: reason?.trim() || null });
      toast.add({
        severity: 'success',
        summary: status === StaffStatus.Inactive ? 'Staff deactivated' : 'Staff reactivated',
        detail: status === StaffStatus.Inactive
          ? 'Their roles in this school were revoked and they can no longer enter it.'
          : 'They can enter this school again. Grant roles deliberately — none were restored.',
        life: 5000,
      });
      return true;
    } catch (e) {
      handleServerErrors(e);
      toast.add({
        severity: 'error',
        summary: 'Could not update status',
        detail: getFirstErrorMessage() || extractGraphQLError(e, 'Please try again.'),
        life: 5000,
      });
      return false;
    } finally {
      updatingId.value = null;
    }
  }

  const deactivate = (staffId: string, reason?: string | null) => setStatus(staffId, StaffStatus.Inactive, reason);
  const reactivate = (staffId: string, reason?: string | null) => setStatus(staffId, StaffStatus.Active, reason);

  return { updatingId, serverErrors, setStatus, deactivate, reactivate };
}
